import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { AppLayout } from '../components/layout/AppLayout';
import { db } from '../lib/firebase';
import { collection, getDocs, orderBy, query } from 'firebase/firestore';
import { 
  PlusIcon, 
  FolderIcon, 
  FunnelIcon,
  MagnifyingGlassIcon,
  CalendarIcon,
  TagIcon,
  ChartBarIcon
} from '@heroicons/react/24/outline';
import { FlamingoIcon } from '../components/common/FlamingoBrand';
import { BrandGradient } from '../components/common/PFILogo';
import { formatProjectName } from '../utils/projectUtils';
import { formatDate } from '../utils/dateFormatters';

export default function ProjectsPage() {
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    loadProjects();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [projects, searchTerm, statusFilter, categoryFilter, dateFilter]);

  const loadProjects = async () => {
    try {
      setLoading(true);
      const q = query(collection(db, 'projects'), orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      
      const projectsData = [];
      querySnapshot.forEach((doc) => {
        projectsData.push({
          id: doc.id,
          ...doc.data()
        });
      });
      
      setProjects(projectsData);
    } catch (error) {
      console.error('Error loading projects:', error);
      setError('Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  // Derive project category from description or project name
  const getProjectCategory = (project) => {
    const text = `${project.name || ''} ${project.description || ''} ${project.projectDescription || ''}`.toLowerCase();
    
    if (text.includes('chair') || text.includes('seating')) return 'furniture';
    if (text.includes('lamp') || text.includes('lighting') || text.includes('light')) return 'lighting';
    if (text.includes('kitchen') || text.includes('dining')) return 'kitchen';
    if (text.includes('bedroom') || text.includes('bed')) return 'bedroom';
    if (text.includes('living') || text.includes('lounge')) return 'living_room';
    if (text.includes('bathroom') || text.includes('bath')) return 'bathroom';
    if (text.includes('office') || text.includes('study')) return 'office';
    if (text.includes('commercial') || text.includes('business')) return 'commercial';
    if (text.includes('outdoor') || text.includes('patio') || text.includes('garden')) return 'outdoor';
    if (text.includes('restoration') || text.includes('antique') || text.includes('vintage')) return 'restoration';
    if (text.includes('custom') || text.includes('bespoke')) return 'custom';
    
    return 'general';
  };

  // Get unique categories from projects
  const getAvailableCategories = () => {
    const categories = projects.map(getProjectCategory);
    return [...new Set(categories)].sort();
  };

  // Apply filters to projects
  const applyFilters = () => {
    let filtered = [...projects];

    // Search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(project =>
        project.name?.toLowerCase().includes(search) ||
        project.description?.toLowerCase().includes(search) ||
        project.customer?.name?.toLowerCase().includes(search) ||
        project.customer?.email?.toLowerCase().includes(search)
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(project => project.status === statusFilter);
    }

    // Category filter
    if (categoryFilter !== 'all') {
      filtered = filtered.filter(project => getProjectCategory(project) === categoryFilter);
    }

    // Date filter
    if (dateFilter !== 'all') {
      const now = new Date();
      const cutoffDate = new Date();
      
      switch (dateFilter) {
        case 'today':
          cutoffDate.setHours(0, 0, 0, 0);
          break;
        case 'week':
          cutoffDate.setDate(now.getDate() - 7);
          break;
        case 'month':
          cutoffDate.setMonth(now.getMonth() - 1);
          break;
        case 'quarter':
          cutoffDate.setMonth(now.getMonth() - 3);
          break;
        case 'year':
          cutoffDate.setFullYear(now.getFullYear() - 1);
          break;
      }
      
      if (dateFilter !== 'all') {
        filtered = filtered.filter(project => {
          const projectDate = project.createdAt?.seconds 
            ? new Date(project.createdAt.seconds * 1000)
            : new Date(project.createdAt);
          return projectDate >= cutoffDate;
        });
      }
    }

    setFilteredProjects(filtered);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setCategoryFilter('all');
    setDateFilter('all');
  };

  const formatCategoryLabel = (category) => {
    return category.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-gradient-to-r from-green-400 to-emerald-500 text-white';
      case 'in_progress':
        return 'bg-gradient-to-r from-blue-400 to-indigo-500 text-white';
      case 'planning':
        return 'bg-gradient-to-r from-orange-400 to-pink-500 text-white';
      default:
        return 'bg-gradient-to-r from-gray-400 to-gray-500 text-white';
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-500 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading projects...</p>
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="max-w-7xl">
        {/* Header */}
        <BrandGradient className="rounded-xl p-6 mb-8 text-white shadow-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <FlamingoIcon className="w-10 h-10 mr-4 text-white filter drop-shadow-sm" />
              <div>
                <h1 className="text-3xl font-bold mb-1">Projects</h1>
                <p className="text-pink-100">
                  Manage your interior design projects
                </p>
              </div>
            </div>
            <Link
              to="/projects/new"
              className="inline-flex items-center px-6 py-3 bg-white text-pink-600 font-semibold text-sm rounded-lg hover:shadow-lg transition-all duration-300 pfi-button"
            >
              <PlusIcon className="w-4 h-4 mr-2" />
              New Project
            </Link>
          </div>
        </BrandGradient>

        {/* Filter Controls */}
        <div className="mb-6">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              {/* Search */}
              <div className="flex-1 max-w-md">
                <div className="relative">
                  <MagnifyingGlassIcon className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search projects, customers..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Filter Toggle */}
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className={`flex items-center px-4 py-2 rounded-lg border transition-colors ${
                    showFilters
                      ? 'bg-pink-50 border-pink-200 text-pink-700'
                      : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <FunnelIcon className="w-4 h-4 mr-2" />
                  Filters
                </button>
                
                {/* Active filter count */}
                {(statusFilter !== 'all' || categoryFilter !== 'all' || dateFilter !== 'all') && (
                  <span className="px-2 py-1 bg-pink-100 text-pink-800 text-xs rounded-full">
                    {[statusFilter, categoryFilter, dateFilter].filter(f => f !== 'all').length} active
                  </span>
                )}
                
                <div className="text-sm text-gray-600">
                  {filteredProjects.length} of {projects.length} projects
                </div>
              </div>
            </div>

            {/* Expanded Filters */}
            {showFilters && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Status Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <ChartBarIcon className="w-4 h-4 inline mr-1" />
                      Status
                    </label>
                    <select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                    >
                      <option value="all">All Statuses</option>
                      <option value="planning">Planning</option>
                      <option value="in_progress">In Progress</option>
                      <option value="completed">Completed</option>
                      <option value="on_hold">On Hold</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </div>

                  {/* Category Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <TagIcon className="w-4 h-4 inline mr-1" />
                      Category
                    </label>
                    <select
                      value={categoryFilter}
                      onChange={(e) => setCategoryFilter(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                    >
                      <option value="all">All Categories</option>
                      {getAvailableCategories().map(category => (
                        <option key={category} value={category}>
                          {formatCategoryLabel(category)}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Date Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <CalendarIcon className="w-4 h-4 inline mr-1" />
                      Created
                    </label>
                    <select
                      value={dateFilter}
                      onChange={(e) => setDateFilter(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                    >
                      <option value="all">All Time</option>
                      <option value="today">Today</option>
                      <option value="week">Last 7 Days</option>
                      <option value="month">Last Month</option>
                      <option value="quarter">Last 3 Months</option>
                      <option value="year">Last Year</option>
                    </select>
                  </div>
                </div>

                {/* Clear Filters */}
                {(searchTerm || statusFilter !== 'all' || categoryFilter !== 'all' || dateFilter !== 'all') && (
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <button
                      onClick={clearFilters}
                      className="text-sm text-pink-600 hover:text-pink-800 font-medium"
                    >
                      Clear all filters
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Error State */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6">
            {error}
          </div>
        )}

        {/* Projects Grid */}
        {projects.length === 0 ? (
          <div className="text-center py-12">
            <FolderIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No projects yet</h3>
            <p className="text-gray-500 mb-6">Get started by creating your first project</p>
            <Link
              to="/projects/new"
              className="inline-flex items-center px-4 py-2 bg-pink-600 text-white text-sm font-medium rounded-md hover:bg-pink-700 transition-colors"
            >
              <PlusIcon className="w-4 h-4 mr-2" />
              Create First Project
            </Link>
          </div>
        ) : filteredProjects.length === 0 ? (
          <div className="text-center py-12">
            <FolderIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No projects match your filters</h3>
            <p className="text-gray-500 mb-6">Try adjusting your search criteria or clear filters</p>
            <button
              onClick={clearFilters}
              className="inline-flex items-center px-4 py-2 bg-pink-600 text-white text-sm font-medium rounded-md hover:bg-pink-700 transition-colors"
            >
              Clear Filters
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredProjects.map((project) => (
              <div key={project.id} className="bg-white rounded-xl shadow-md border border-pink-100 p-6 hover:shadow-xl hover:scale-105 transition-all duration-300 pfi-card">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1 pr-2">
                    <h3 className="text-lg font-semibold text-gray-900 truncate">
                      {formatProjectName(project)}
                    </h3>
                    <div className="flex items-center mt-1">
                      <TagIcon className="w-3 h-3 text-gray-400 mr-1" />
                      <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                        {formatCategoryLabel(getProjectCategory(project))}
                      </span>
                    </div>
                  </div>
                  <span className={`px-3 py-1 text-xs font-semibold rounded-full shadow-sm ${getStatusColor(project.status)}`}>
                    {project.status?.replace('_', ' ') || 'planning'}
                  </span>
                </div>

                {project.description && (
                  <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                    {project.description}
                  </p>
                )}

                <div className="space-y-2 mb-4">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500">Customer:</span>
                    <span className="font-medium text-gray-900">
                      {project.customer?.name || 'N/A'}
                    </span>
                  </div>
                  {project.budget > 0 && (
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-500">Budget:</span>
                      <span className="font-medium text-gray-900">
                        {formatCurrency(project.budget)}
                      </span>
                    </div>
                  )}
                  {project.createdAt && (
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-500">Created:</span>
                      <span className="text-gray-700">
                        {formatDate(project.createdAt)}
                      </span>
                    </div>
                  )}
                </div>

                <div className="flex items-center justify-between">
                  <Link
                    to={`/projects/${project.id}`}
                    className="text-pink-600 hover:text-pink-700 text-sm font-medium"
                  >
                    View Details →
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Stats */}
        {projects.length > 0 && (
          <div className="mt-8 grid grid-cols-1 md:grid-cols-5 gap-4">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="text-2xl font-bold text-gray-900">{filteredProjects.length}</div>
              <div className="text-sm text-gray-600">Filtered Projects</div>
              {filteredProjects.length !== projects.length && (
                <div className="text-xs text-gray-400">of {projects.length} total</div>
              )}
            </div>
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="text-2xl font-bold text-orange-600">
                {filteredProjects.filter(p => p.status === 'planning').length}
              </div>
              <div className="text-sm text-gray-600">Planning</div>
            </div>
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="text-2xl font-bold text-blue-600">
                {filteredProjects.filter(p => p.status === 'in_progress').length}
              </div>
              <div className="text-sm text-gray-600">In Progress</div>
            </div>
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="text-2xl font-bold text-green-600">
                {filteredProjects.filter(p => p.status === 'completed').length}
              </div>
              <div className="text-sm text-gray-600">Completed</div>
            </div>
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="text-2xl font-bold text-pink-600">
                {formatCurrency(filteredProjects.reduce((sum, p) => sum + (p.budget || 0), 0))}
              </div>
              <div className="text-sm text-gray-600">Budget Value</div>
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
}